// ---------------------------------------------------------------------------
// The overview's four figures, and the one count behind "Leads contacted" (R15c)
//
// The third figure on the overview (dashboard.ts) was "Messages sent". Per
// campaign it added `totalSent` for an email campaign, `totalDmSent` for a
// LinkedIn one and both for a cross-channel one: two measures on two clocks.
// `totalSent` is the number of leads something was sent to (`sentAt`: an email,
// a connection request or a message), counted from the leads on every
// GET /api/campaigns (server/helpers/campaign-live-counts.ts). `totalDmSent` is
// a number of LinkedIn messages that only a sync refreshes, and the list passes
// it through as stored. So the tile added leads to messages and a live count to
// a stale one, and for a LinkedIn campaign it disagreed with the Sent column of
// the same page, which counts the leads, connection requests included.
//
// Ferenc, 29 October, option A: one measure, live, the column's. The tile adds
// up the live count over every campaign, whatever its channel, and is called
// what it counts. "Leads contacted" is the campaign page's word for a lead
// something was sent to (its "Not contacted yet" card: "Nothing has been sent to
// them yet"), and the line under the figure says which sends count. The Sent
// column and the tile read the count through leadsContacted, so the two cannot
// drift apart again.
//
// A campaign that came without its count is not a zero. Its cell and the tile
// say "—", and loadOverview (dashboard-load.ts) names it in the console.
//
// Since R15b the tiles are the system's MetricTile (design/metrics.ts), as in
// ConsoleOverview, without a comparison (7.9: the API keeps no earlier
// period, so no delta and no trend), never more than four in a row: one
// column on a phone, two from 448px of the page's width, four from 896px
// (the page is the container, so the SideNav's 240px count). While the lists
// load, four of the system's metric skeletons stand in a busy region.
//
// Only Lit, no Adornis, so the sums are tested by behaviour and the tiles in
// the DOM suite (tests/dashboard-figures.test.ts, tests/dom/dashboard-figures.dom.ts).
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';

import { skeleton } from '../design/feedback.js';
import { metricTile } from '../design/metrics.js';

/** What the third figure is called: what it counts. */
export const CONTACTED_LABEL = 'Leads contacted';
/** Under the figure: which sends make a lead contacted. */
export const CONTACTED_HINT = 'Any channel, connection requests included';
/** Under "—", when a campaign came without its count. */
export const NOT_COUNTED_HINT = 'Not every campaign could be counted';

/** 9,332 rather than 9332: the design system's number format. */
export const formatCount = (n: number) => n.toLocaleString('en-US');

/**
 * The leads of one campaign something was sent to, on any channel: the live
 * count GET /api/campaigns attaches. The one place the overview reads it; the
 * Sent column and the Leads contacted tile both go through here. Anything that
 * is not a count (missing, negative, a fraction, not a number) is undefined,
 * never 0.
 */
export function leadsContacted(campaign: { totalSent?: unknown } | null | undefined): number | undefined {
  const count = campaign?.totalSent;
  return typeof count === 'number' && Number.isInteger(count) && count >= 0 ? count : undefined;
}

/** The fields of a campaign the four figures read. */
export interface FigureCampaign {
  leadsProcessed?: number;
  totalSent?: unknown;
  totalReplied?: number;
}

/** The overview's four headline figures, over the campaigns in view (archived ones too). */
export interface OverviewFigures {
  campaigns: number;
  leads: number;
  /** The Sent column, summed; null when a campaign came without its count. */
  contacted: number | null;
  replies: number;
}

export function overviewFigures(campaigns: readonly FigureCampaign[]): OverviewFigures {
  let contacted: number | null = 0;
  for (const campaign of campaigns) {
    const count = leadsContacted(campaign);
    contacted = count === undefined || contacted === null ? null : contacted + count;
  }
  return {
    campaigns: campaigns.length,
    leads: campaigns.reduce((s, c) => s + (c.leadsProcessed ?? 0), 0),
    contacted,
    replies: campaigns.reduce((s, c) => s + (c.totalReplied ?? 0), 0),
  };
}

/** The ids of the campaigns that came without their live count. */
export function uncountedCampaigns(campaigns: readonly { _id: string; totalSent?: unknown }[]): string[] {
  return campaigns.filter(c => leadsContacted(c) === undefined).map(c => c._id);
}

/** The grid of the four tiles, as the page's width allows (the page is an @container). */
const TILE_GRID = 'grid grid-cols-1 gap-6 @md:grid-cols-2 @4xl:grid-cols-4';

/** The four headline figures as the system's MetricTiles, never more than four in a row. */
export function overviewTiles(f: OverviewFigures): TemplateResult {
  // The figure is formatted here, not by the tile: 9,332 as the column writes it (formatCount, en-US).
  return html`<div class=${TILE_GRID}>
    ${metricTile({ label: 'Campaigns', value: formatCount(f.campaigns) })}
    ${metricTile({ label: 'Leads', value: formatCount(f.leads) })}
    ${metricTile({
      label: CONTACTED_LABEL,
      value: f.contacted === null ? '—' : formatCount(f.contacted),
      hint: f.contacted === null ? NOT_COUNTED_HINT : CONTACTED_HINT,
    })}
    ${metricTile({ label: 'Replies', value: formatCount(f.replies) })}
  </div>`;
}

/** Where the four tiles will be, while the lists load: four metric skeletons, one of them named. */
export function overviewTilesLoading(): TemplateResult {
  return html`<div class=${TILE_GRID} aria-busy="true">
    ${skeleton({ variant: 'metric', label: 'Loading the figures' })} ${skeleton({ variant: 'metric' })}
    ${skeleton({ variant: 'metric' })} ${skeleton({ variant: 'metric' })}
  </div>`;
}
